import React, { useState } from "react";
import { IconButton, CircularProgress, Avatar } from "@mui/material";
import { observer } from "mobx-react-lite";
import { useStore } from "../../helpers";

import AddCircleRoundedIcon from "@mui/icons-material/AddCircleRounded";
import DeleteIcon from "@mui/icons-material/Delete";

import OfficePersonAutoComplete from "../../new-features/service-requests/sr-settings/assignees/office-person-auto-complete";
import { useMutation } from "@tanstack/react-query";
import { toJS } from "mobx";
import { toast } from "react-toastify";

import { useAssignee } from "../../new-features/service-requests/sr-settings/assignees/hooks";
import { useParams } from "react-router-dom";
import { FormProvider, useForm } from "react-hook-form";
import { DeleteModal } from "../../components";
import { safe_get } from "../../report-visuals";
import ThumbnailImage from "../../components-new/Thumbnail";

export const ReporteesSection = observer(() => {
  const { userStore } = useStore();
  const { activeUser, delete_Reportee_manager, active_reportee } = userStore;

  const { projectid } = useParams<{ projectid: string }>();
  const numericProjectId = parseInt(projectid || "0", 10);
  const numericUserId = parseInt(activeUser?.id || 0, 10);

  const repid = safe_get(active_reportee, "id", null);

  const [showAddField, setShowAddField] = useState(false);

  const { data: officeUserData = [] } = useAssignee();

  const reportees = toJS(activeUser?.reportee) || [];

  const filteredOfficeUsers = officeUserData.filter(
    (u: any) => !reportees.some((rep: any) => rep.id === u.id)
  );

  const methods = useForm({
    defaultValues: { reportees: [] },
  });

  const { mutate } = useMutation({
    mutationFn: async (payload: any) => {
      return await userStore.remove_manager_reportees(payload);
    },

    onSuccess: async () => {
      await userStore.get_user_permissions_and_parts({
        userid: numericUserId,
        projectid: numericProjectId,
      });

      toast.success("Reportee deleted successfully!");
      userStore.setactiveReportee(null);
      userStore.set_delete_repor_manager(false);
    },

    onError: () => {
      toast.error("Something went wrong while deleting");
    },
  });

  const { mutateAsync, isLoading: mutationLoading } = useMutation({
    mutationFn: async (payload: any) => {
      return await userStore.add_reportees_list(payload);
    },

    onSuccess: async () => {
      await userStore.get_user_permissions_and_parts({
        userid: numericUserId,
        projectid: numericProjectId,
      });

      toast.success("Reportees updated successfully!");
      setShowAddField(false);
      methods.reset();
    },
  });

  const onSubmit = async (data: any) => {
    const payload = {
      projectid: numericProjectId,
      mgrid: numericUserId,
      reportees: data.reportees.map((u: any) => u.id),
    };

    await mutateAsync(payload);
  };

  const handleRepDelete = (rep: any) => {
    userStore.setactiveReportee(rep);
    userStore.set_delete_repor_manager(true);
  };

  const onCancel = () => {
    userStore.set_delete_repor_manager(false);
  };

  return (
    <div className="mt-6">
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-gray-800 font-semibold">Reportees</h4>

        <button
          onClick={() => setShowAddField((prev) => !prev)}
          className="p-1 rounded-full hover:bg-indigo-50 transition"
        >
          <AddCircleRoundedIcon className="text-indigo-600" />
        </button>
      </div>

      {showAddField && (
        <FormProvider {...methods}>
          <form
            onSubmit={methods.handleSubmit(onSubmit)}
            className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm w-full max-w-xl mx-4"
          >
            <label className="text-sm font-medium text-gray-700 block mb-3">
              Select Reportees
            </label>

            <div className="flex items-start gap-4">
              <div className="flex-1">
                <OfficePersonAutoComplete
                  name="reportees"
                  label=""
                  multi
                  officeUserData={filteredOfficeUsers}
                />
              </div>

              <div className="flex flex-row gap-x-2 items-start mt-2">
                <button
                  type="submit"
                  disabled={mutationLoading}
                  className={`px-5 py-2 rounded-lg text-sm font-medium text-white w-20 text-center
          ${
            mutationLoading
              ? "bg-indigo-300"
              : "bg-indigo-600 hover:bg-indigo-700"
          }`}
                >
                  {mutationLoading ? (
                    <CircularProgress size={18} color="inherit" />
                  ) : (
                    "Add"
                  )}
                </button>

                <button
                  type="button"
                  onClick={() => {
                    methods.reset();
                    setShowAddField(false);
                  }}
                  className={`px-5 py-2 rounded-lg text-sm font-medium text-white w-20 text-center
          ${mutationLoading ? "bg-red-500" : "bg-red-500 hover:bg-white-500"}`}
                >
                  Cancel
                </button>
              </div>
            </div>
          </form>
        </FormProvider>
      )}

      {reportees.length === 0 && (
        <p className="text-sm text-gray-400 mt-3">No reportees assigned</p>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
        {reportees.map((rep: any) => (
          <div
            key={rep.id}
            className="border border-gray-200 bg-white rounded-xl px-4 py-3 shadow-sm group transition"
          >
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <Avatar sx={{ width: 42, height: 42 }} alt={rep?.name}>
                  {rep?.profileuri && (
                    <ThumbnailImage
                      key={rep?.id}
                      s3Key={rep?.profileuri}
                      bucket="urn-assets"
                      width={42}
                      height={42}
                    />
                  )}
                </Avatar>

                <div>
                  <p className="font-semibold text-gray-800 text-[15px] leading-tight">
                    {rep?.name}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">ID: {rep?.id}</p>
                </div>
              </div>

              <IconButton
                size="small"
                onClick={() => handleRepDelete(rep)}
                className="opacity-0 group-hover:opacity-100 hover:bg-red-50 transition"
              >
                <DeleteIcon fontSize="small" className="text-red-600" />
              </IconButton>
            </div>
          </div>
        ))}
      </div>

      {delete_Reportee_manager && (
        <DeleteModal
          onDeleteClick={() => {
            const repId = safe_get(userStore.active_reportee, "id", null);

            mutate({
              projectid: numericProjectId,
              mgrid: numericUserId,
              reportee: repId,
            });
          }}
          onCancelClick={onCancel}
          isOpen={delete_Reportee_manager}
          id={active_reportee}
          desc="Do you want to delete this Reportee?"
        />
      )}
    </div>
  );
});
